import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { Skill, Submission, SubmissionDetail } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { FileUpload } from '../components/ui/FileUpload';
import { Trash2, Plus } from 'lucide-react';

export const SubmissionForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [periodType, setPeriodType] = useState<'Monthly'>('Monthly');
    const [period, setPeriod] = useState('');
    const [details, setDetails] = useState<SubmissionDetail[]>([]);
    const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
    const [status, setStatus] = useState<'Draft' | 'Submitted' | 'Reviewed'>('Draft');
    const [skillSearchTexts, setSkillSearchTexts] = useState<string[]>([]); // For searchable skill inputs
    const [categoryDropdownStates, setCategoryDropdownStates] = useState<boolean[]>([]); // Category dropdown open/close per row
    const [selectedCategoriesPerRow, setSelectedCategoriesPerRow] = useState<string[]>([]); // Selected category per row
    const [skillDropdownStates, setSkillDropdownStates] = useState<boolean[]>([]); // Skill dropdown open/close per row
    const [categoryFilterTexts, setCategoryFilterTexts] = useState<string[]>([]); // Category search filter per row
    const [skillFilterTexts, setSkillFilterTexts] = useState<string[]>([]); // Skill search filter per row

    useEffect(() => {
        apiService.getSkills().then(setAvailableSkills);
        if (isEdit && id) {
            apiService.getSubmissionById(id).then((sub) => {
                if (sub) {
                    // Force period type to 'Monthly' even if backend returns 'Quarterly'
                    setPeriodType('Monthly');
                    setPeriod(sub.period);
                    setDetails(sub.details);
                    setStatus(sub.status as any);
                }
            });
        } else {
            // Default period
            const now = new Date();
            setPeriod(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
        }
    }, [id, isEdit]);

    // Initialize category dropdown states when skills are loaded and details are available
    useEffect(() => {
        if (availableSkills.length > 0 && details.length > 0) {
            // Initialize category selections based on the skills in details
            const categories = details.map(detail => {
                const skill = availableSkills.find(s => s.id === detail.skillId);
                return skill?.category || '';
            });
            setSelectedCategoriesPerRow(categories);

            // Initialize other dropdown states if not already initialized
            if (categoryDropdownStates.length === 0) {
                setCategoryDropdownStates(new Array(details.length).fill(false));
                setSkillDropdownStates(new Array(details.length).fill(false));
                setCategoryFilterTexts(new Array(details.length).fill(''));
                setSkillFilterTexts(new Array(details.length).fill(''));
                setSkillSearchTexts(new Array(details.length).fill(''));
            }
        }
    }, [availableSkills, details]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.dropdown-container')) {
                setCategoryDropdownStates(prev => prev.map(() => false));
                setSkillDropdownStates(prev => prev.map(() => false));
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleAddSkill = () => {
        setDetails([...details, { skillId: '', selfScore: 1, achievementNotes: '' }]);
        setSkillSearchTexts([...skillSearchTexts, '']);
        setCategoryDropdownStates([...categoryDropdownStates, false]);
        setSelectedCategoriesPerRow([...selectedCategoriesPerRow, '']);
        setSkillDropdownStates([...skillDropdownStates, false]);
        setCategoryFilterTexts([...categoryFilterTexts, '']);
        setSkillFilterTexts([...skillFilterTexts, '']);
    };

    const handleRemoveSkill = (index: number) => {
        setDetails(details.filter((_, i) => i !== index));
        setSkillSearchTexts(skillSearchTexts.filter((_, i) => i !== index));
        setCategoryDropdownStates(categoryDropdownStates.filter((_, i) => i !== index));
        setSelectedCategoriesPerRow(selectedCategoriesPerRow.filter((_, i) => i !== index));
        setSkillDropdownStates(skillDropdownStates.filter((_, i) => i !== index));
        setCategoryFilterTexts(categoryFilterTexts.filter((_, i) => i !== index));
        setSkillFilterTexts(skillFilterTexts.filter((_, i) => i !== index));
    };

    const handleDetailChange = (index: number, field: keyof SubmissionDetail, value: any) => {
        const newDetails = [...details];
        newDetails[index] = { ...newDetails[index], [field]: value };
        setDetails(newDetails);
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    };

    const handleFilesSelected = async (index: number, files: File[]) => {
        const newDetails = [...details];
        const existingCertificates = newDetails[index].certificates || [];

        const newCertificates = await Promise.all(
            files.map(async (file) => ({
                name: file.name,
                size: file.size,
                type: file.type,
                dataUrl: await fileToBase64(file),
                uploadedAt: new Date().toISOString(),
            }))
        );

        newDetails[index] = {
            ...newDetails[index],
            certificates: [...existingCertificates, ...newCertificates],
        };
        setDetails(newDetails);
    };

    const handleRemoveFile = (skillIndex: number, fileIndex: number) => {
        const newDetails = [...details];
        const certificates = newDetails[skillIndex].certificates || [];
        newDetails[skillIndex] = {
            ...newDetails[skillIndex],
            certificates: certificates.filter((_, i) => i !== fileIndex),
        };
        setDetails(newDetails);
    };

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
        let isValid = true;

        // Period validation
        if (!period) {
            newErrors.period = 'Period is required';
            isValid = false;
        } else if (periodType === 'Monthly' && !/^\d{4}-\d{2}$/.test(period)) {
            newErrors.period = 'Format must be YYYY-MM';
            isValid = false;
        }

        // Skills validation
        if (details.length === 0) {
            newErrors.skills = 'At least one skill is required';
            isValid = false;
        }

        // Check for duplicate skills
        const skillIds = details.map(d => d.skillId);
        const uniqueSkillIds = new Set(skillIds);
        if (skillIds.length !== uniqueSkillIds.size) {
            newErrors.skills = 'Duplicate skills are not allowed';
            isValid = false;
        }

        details.forEach((detail, index) => {
            if (!detail.skillId) {
                newErrors[`skill_${index}`] = 'Skill selection is required';
                isValid = false;
            }
            if (detail.selfScore < 1 || detail.selfScore > 5) {
                newErrors[`score_${index}`] = 'Score must be between 1 and 5';
                isValid = false;
            }
            if (!detail.achievementNotes || detail.achievementNotes.length < 10) {
                newErrors[`notes_${index}`] = 'Achievement notes must be at least 10 characters';
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (newStatus: 'Draft' | 'Submitted') => {
        if (!user) return;

        // Only validate strictly for "Submitted" status, but basic checks for Draft
        if (newStatus === 'Submitted' && !validateForm()) {
            return;
        }

        const submissionData = {
            engineerId: user.id,
            engineerName: user.name,
            periodType,
            period,
            status: newStatus,
            details,
        };

        try {
            if (isEdit && id) {
                await apiService.updateSubmission(id, submissionData);
            } else {
                await apiService.createSubmission(submissionData);
            }
            navigate('/engineer/dashboard');
        } catch (error) {
            console.error('Failed to save submission', error);
        }
    };

    // Extract unique categories from available skills
    const uniqueCategories = Array.from(new Set(availableSkills.map(s => s.category))).sort();

    // Get filtered skills for a specific row based on selected category
    const getFilteredSkillsForRow = (rowIndex: number) => {
        const selectedCategory = selectedCategoriesPerRow[rowIndex];
        return selectedCategory
            ? availableSkills.filter(s => s.category === selectedCategory)
            : availableSkills;
    };

    const isReadOnly = status === 'Reviewed';

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit Submission' : 'New Submission'}</h1>
                {isReadOnly && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        Read Only - Reviewed
                    </span>
                )}
            </div>

            {isReadOnly && (
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-md">
                    <div className="flex">
                        <div className="ml-3">
                            <p className="text-sm text-blue-700">
                                This submission has been reviewed by your manager and can no longer be edited.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <Card>
                <CardHeader>
                    <CardTitle>Period Details</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                        <select
                            className="w-full rounded-md border border-gray-300 p-2"
                            value={periodType}
                            onChange={(e) => setPeriodType(e.target.value as any)}
                            disabled={isEdit || isReadOnly}
                        >
                            <option value="Monthly">Monthly</option>

                        </select>
                    </div>
                    <Input
                        label="Period (YYYY-MM)"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        disabled={isReadOnly}
                        error={errors.period}
                    />
                </CardContent>
            </Card>

            <div className="space-y-4">
                {errors.skills && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-md">
                        <div className="flex">
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{errors.skills}</p>
                            </div>
                        </div>
                    </div>
                )}

                {details.map((detail, index) => (
                    <Card key={index} className={errors[`skill_${index}`] || errors[`score_${index}`] || errors[`notes_${index}`] ? 'border-red-300 ring-1 ring-red-300' : ''}>
                        <CardContent className="pt-6">
                            <div className="grid gap-4 sm:grid-cols-12">
                                {/* Category Dropdown */}
                                <div className="sm:col-span-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                    <div className="relative dropdown-container">
                                        <div
                                            className={`w-full rounded-md border p-2 bg-white text-gray-900 min-h-[42px] cursor-pointer hover:border-gray-400 ${errors[`skill_${index}`] ? 'border-red-300' : 'border-gray-300'}`}
                                            onClick={() => {
                                                if (!isReadOnly) {
                                                    const newStates = [...categoryDropdownStates];
                                                    newStates[index] = !newStates[index];
                                                    setCategoryDropdownStates(newStates);
                                                }
                                            }}
                                        >
                                            <span className={selectedCategoriesPerRow[index] ? "text-gray-900" : "text-gray-400"}>
                                                {selectedCategoriesPerRow[index] || "-- All Categories --"}
                                            </span>
                                        </div>

                                        {categoryDropdownStates[index] && !isReadOnly && (
                                            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                                                <div className="p-2 border-b border-gray-200">
                                                    <input
                                                        type="text"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                                        placeholder="Search categories..."
                                                        value={categoryFilterTexts[index] || ''}
                                                        onChange={(e) => {
                                                            const newTexts = [...categoryFilterTexts];
                                                            newTexts[index] = e.target.value;
                                                            setCategoryFilterTexts(newTexts);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                <div className="max-h-60 overflow-y-auto">
                                                    <div
                                                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                                        onClick={() => {
                                                            const newCategories = [...selectedCategoriesPerRow];
                                                            newCategories[index] = '';
                                                            setSelectedCategoriesPerRow(newCategories);
                                                            const newStates = [...categoryDropdownStates];
                                                            newStates[index] = false;
                                                            setCategoryDropdownStates(newStates);
                                                        }}
                                                    >
                                                        <span className="text-sm text-gray-900">-- All Categories --</span>
                                                    </div>
                                                    {uniqueCategories
                                                        .filter(cat => cat.toLowerCase().includes((categoryFilterTexts[index] || '').toLowerCase()))
                                                        .map(category => (
                                                            <div
                                                                key={category}
                                                                className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                                                onClick={() => {
                                                                    const newCategories = [...selectedCategoriesPerRow];
                                                                    newCategories[index] = category;
                                                                    setSelectedCategoriesPerRow(newCategories);
                                                                    const newStates = [...categoryDropdownStates];
                                                                    newStates[index] = false;
                                                                    setCategoryDropdownStates(newStates);
                                                                }}
                                                            >
                                                                <span className="text-sm text-gray-900">{category}</span>
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Skill Dropdown */}
                                <div className="sm:col-span-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Skill</label>
                                    <div className="relative dropdown-container">
                                        <div
                                            className={`w-full rounded-md border p-2 bg-white text-gray-900 min-h-[42px] cursor-pointer hover:border-gray-400 ${errors[`skill_${index}`] ? 'border-red-300' : 'border-gray-300'}`}
                                            onClick={() => {
                                                if (!isReadOnly) {
                                                    const newStates = [...skillDropdownStates];
                                                    newStates[index] = !newStates[index];
                                                    setSkillDropdownStates(newStates);
                                                }
                                            }}
                                        >
                                            <span className={detail.skillId ? "text-gray-900" : "text-gray-400"}>
                                                {detail.skillId ? availableSkills.find(s => s.id === detail.skillId)?.name : "-- Select Skill --"}
                                            </span>
                                        </div>

                                        {skillDropdownStates[index] && !isReadOnly && (
                                            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                                                <div className="p-2 border-b border-gray-200">
                                                    <input
                                                        type="text"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        placeholder="Search skills..."
                                                        value={skillFilterTexts[index] || ''}
                                                        onChange={(e) => {
                                                            const newTexts = [...skillFilterTexts];
                                                            newTexts[index] = e.target.value;
                                                            setSkillFilterTexts(newTexts);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                <div className="max-h-60 overflow-y-auto">
                                                    {getFilteredSkillsForRow(index)
                                                        .filter(skill => skill.name.toLowerCase().includes((skillFilterTexts[index] || '').toLowerCase()))
                                                        .map(skill => (
                                                            <div
                                                                key={skill.id}
                                                                className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                                                onClick={() => {
                                                                    handleDetailChange(index, 'skillId', skill.id);
                                                                    const newStates = [...skillDropdownStates];
                                                                    newStates[index] = false;
                                                                    setSkillDropdownStates(newStates);
                                                                }}
                                                            >
                                                                <span className="text-sm text-gray-900">{skill.name}</span>
                                                            </div>
                                                        ))}
                                                    {getFilteredSkillsForRow(index).filter(skill => skill.name.toLowerCase().includes((skillFilterTexts[index] || '').toLowerCase())).length === 0 && (
                                                        <div className="px-3 py-2 text-sm text-gray-500">No skills found</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {errors[`skill_${index}`] && <p className="mt-1 text-xs text-red-600">{errors[`skill_${index}`]}</p>}
                                </div>
                                <div className="sm:col-span-3">
                                    <Input
                                        label="Score (1-5)"
                                        type="number"
                                        min="1"
                                        max="5"
                                        value={detail.selfScore}
                                        onChange={(e) => handleDetailChange(index, 'selfScore', parseInt(e.target.value))}
                                        error={errors[`score_${index}`]}
                                        disabled={isReadOnly}
                                    />
                                </div>
                                <div className="sm:col-span-11">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Achievements</label>
                                    <textarea
                                        className={`w-full rounded-md border p-2 text-sm ${errors[`notes_${index}`] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`}
                                        rows={2}
                                        value={detail.achievementNotes}
                                        onChange={(e) => handleDetailChange(index, 'achievementNotes', e.target.value)}
                                        disabled={isReadOnly}
                                    />
                                    {errors[`notes_${index}`] && <p className="mt-1 text-xs text-red-600">{errors[`notes_${index}`]}</p>}
                                </div>
                                {!isReadOnly && (
                                    <div className="sm:col-span-1 flex items-center justify-center pt-6">
                                        <Button variant="ghost" size="sm" onClick={() => handleRemoveSkill(index)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* Certificate Upload Section */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Certificates / Supporting Documents
                                </label>
                                {isReadOnly ? (
                                    <div className="space-y-2">
                                        {detail.certificates?.map((cert, i) => (
                                            <div key={i} className="flex items-center text-sm text-gray-600">
                                                <span className="mr-2">📄</span>
                                                {cert.name}
                                            </div>
                                        ))}
                                        {(!detail.certificates || detail.certificates.length === 0) && (
                                            <span className="text-sm text-gray-500 italic">No certificates attached</span>
                                        )}
                                    </div>
                                ) : (
                                    <FileUpload
                                        onFilesSelected={(files) => handleFilesSelected(index, files)}
                                        existingFiles={detail.certificates || []}
                                        onRemoveFile={(fileIndex) => handleRemoveFile(index, fileIndex)}
                                        maxFiles={5}
                                        maxSizeMB={5}
                                    />
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {!isReadOnly && (
                <div className="flex justify-center">
                    <Button variant="outline" onClick={handleAddSkill} className="w-full border-dashed">
                        <Plus className="mr-2 h-4 w-4" /> Add Skill
                    </Button>
                </div>
            )}

            <div className="flex justify-end space-x-4">
                <Button variant="secondary" onClick={() => navigate('/engineer/dashboard')}>
                    {isReadOnly ? 'Back to Dashboard' : 'Cancel'}
                </Button>
                {!isReadOnly && (
                    <>
                        <Button variant="outline" onClick={() => handleSubmit('Draft')}>
                            Save Draft
                        </Button>
                        <Button onClick={() => handleSubmit('Submitted')}>
                            Submit
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};
